import React, { useState } from 'react';
import { Plus, Minus, HelpCircle, Brain, Bot, Layers, Shield, Clock, Users } from 'lucide-react';
import { faqItemsData } from './saasContent';

const faqIcons = [Brain, Bot, Layers, Shield, Clock, Users];

const SaasFAQSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const faqItems = faqItemsData.map((item, index) => {
    const IconComponent = faqIcons[index] || HelpCircle;
    return { ...item, icon: <IconComponent /> };
  });

  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItemsData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer.replace(/\n\n•/g, ' •').replace(/\n/g, ' '),
      },
    })),
  };

  return (
    <section id="faq" className="py-12 md:py-20 bg-gray-100">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14 max-w-2xl mx-auto px-4">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-black/80 mb-6">
            Frequently asked questions
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-[2.125rem] font-semibold text-black tracking-tight leading-snug mb-4">
            Questions before you activate?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Clear answers on the Company Brain, AI agents, and how Kolanut helps SaaS teams grow and retain.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-start">
          {faqItems.map((item, index) => (
            <div
              key={`faq-${item.id}-${index}`}
              className="bg-white border border-gray-100 rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg self-start"
            >
              <div
                className="p-5 md:p-6 flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(index)}
              >
                <div className="flex items-center pr-4">
                  <div className="bg-[#c4ffa0]/10 rounded-full p-2 mr-3 text-black">
                    {React.cloneElement(item.icon, { className: 'w-5 h-5 text-black' })}
                  </div>
                  <h3 className="font-semibold text-base md:text-lg text-black">{item.question}</h3>
                </div>
                <button
                  type="button"
                  className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${
                    expandedIndex === index
                      ? 'bg-[#c4ffa0]/20 text-black'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  aria-label={expandedIndex === index ? 'Collapse' : 'Expand'}
                >
                  {expandedIndex === index ? <Minus size={18} /> : <Plus size={18} />}
                </button>
              </div>

              {expandedIndex === index && (
                <div className="border-t border-gray-100">
                  <div className="p-5 md:p-6">
                    {item.answer.split('\n\n').map((paragraph, i) => (
                      <React.Fragment key={i}>
                        {paragraph.startsWith('•') ? (
                          <ul className="list-disc pl-5 mb-3 space-y-1">
                            {paragraph.split('\n').map((bulletPoint, j) => (
                              <li key={j} className="text-gray-600">
                                {bulletPoint.replace('• ', '')}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-600 mb-3">{paragraph}</p>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />
    </section>
  );
};

export default SaasFAQSection;
